import { findQuoteTooOld } from 'apiConnector/dashboard/sanitize';
import { IdType } from 'epic/common-form/ReloadFormRequest';
import { dynamicQuoteRequestedThunk, dynamicQuoteTradeCancelledThunk } from 'state/dynamic/dynamicForm.thunks';
import { quotesRemoved } from 'state/quotes/quotes.actions';
import type { Thunk } from 'state/state.common';
import { staticQuoteRequestedThunk, staticQuoteTradeCancelledThunk } from 'state/static/staticForm.thunks';
import type { Collection } from 'state/utils/types';
import type { QuoteWorkspaceType } from 'state/workspaces/model';
import { workspaceTabActivated, workspaceTabRepriceRequested } from 'state/workspaces/workspace.actions';
import { getDateOnlyFromDate } from 'utils/date';
import { getFrontLogger } from 'utils/loggers/FrontLogger';

import { getQuoteType } from '../selectors';
import {
  dashboardByClientFilterChanged,
  dashboardByPinnedFilterChanged,
  dashboardLoaded,
  dashboardObsoleteQuotesDeleted,
  dashboardTileRemoved,
} from './dashboard.actions';
import type { TileStatus } from './model';
import type { ReloadedTileData } from './reloadedTiles/reloadedTiles.model';
import type { QuoteIndexableData } from './selectors';
import type { TileMetadata } from './tiles/tiles.model';

export function tileRemovedThunk(...quoteIds: string[]): Thunk<void> {
  return (dispatch, getState, { selectors: { getTabIdsByQuoteId } }) => {
    const state = getState();

    quoteIds.forEach(quoteId => {
      const tabIds = getTabIdsByQuoteId(state, quoteId);
      const isTab = tabIds.length > 0;

      dispatch(dashboardTileRemoved(quoteId));

      if (!isTab) {
        dispatch(quotesRemoved(undefined, quoteId));
      }
    });

    dispatch(cleanupFiltersThunk());
  };
}

function cleanupFiltersThunk(): Thunk<void> {
  return (
    dispatch,
    getState,
    {
      selectors: {
        dashboard: { getAllFilters, getAllQuotesIndexes },
      },
    },
  ) => {
    const state = getState();
    const { pinnedGroup, client, product } = getAllFilters(state);
    const remainingQuotes = getAllQuotesIndexes(state);

    const remainingQuotesCountsByFilters = countQuotesByPinnedAndClientFilters(
      remainingQuotes,
      pinnedGroup,
      client,
      product,
    );

    if (pinnedGroup && remainingQuotesCountsByFilters.pinnedGroup === 0) {
      dispatch(dashboardByPinnedFilterChanged());
    }
    if (client) {
      if (remainingQuotesCountsByFilters.client === 0) {
        dispatch(dashboardByClientFilterChanged());
      } else if (remainingQuotesCountsByFilters.product === 0) {
        dispatch(dashboardByClientFilterChanged(client));
      }
    }
  };
}

interface QuotesCountsByPinnedAndClientFilters {
  pinnedGroup: number;
  client: number;
  product: number;
}

function countQuotesByPinnedAndClientFilters(
  quotes: QuoteIndexableData[],
  pinnedGroup?: string,
  client?: string,
  product?: string,
): QuotesCountsByPinnedAndClientFilters {
  return quotes.reduce<QuotesCountsByPinnedAndClientFilters>(
    (result, quote) => {
      if (pinnedGroup && quote.customGroup === pinnedGroup) {
        result.pinnedGroup = result.pinnedGroup + 1;
      }
      if (client && quote.clientId === client) {
        result.client = result.client + 1;

        if (product && quote.product === product) {
          result.product = result.product + 1;
        }
      }
      return result;
    },
    <QuotesCountsByPinnedAndClientFilters>{ pinnedGroup: 0, client: 0, product: 0 },
  );
}

export function openQuoteTabThunk(
  quoteId: string,
  externalQuoteId: string,
  workspace: QuoteWorkspaceType,
): Thunk<void> {
  return (
    dispatch,
    getState,
    {
      selectors: {
        dashboard: { getTabFromQuoteIdAndWorkspace },
      },
      navigateTo,
    },
  ) => {
    const state = getState();
    const tab = getTabFromQuoteIdAndWorkspace(state, quoteId, workspace);

    if (tab !== undefined) {
      navigateTo(tab.workspace === 'static' ? '/static' : '/dynamic');
      dispatch(workspaceTabActivated(tab.tabId, tab.workspace, 'quote'));
    } else {
      dispatch(
        workspaceTabRepriceRequested(
          quoteId,
          IdType.QuoteId,
          quoteId,
          null,
          workspace,
          true,
          undefined,
          externalQuoteId,
        ),
      );
    }
  };
}

export function quoteRepriceThunk(quoteId: string, status: TileStatus | undefined): Thunk<void> {
  return (
    dispatch,
    getState,
    {
      selectors: {
        dashboard: { getTabFromQuoteId },
      },
    },
  ) => {
    if (status === undefined) {
      return;
    }
    const state = getState();
    const tab = getTabFromQuoteId(state, quoteId);
    if (tab === undefined) {
      throw new Error('no live tabId associated to this quote, reprice button should have been disabled');
    }
    const quoteType = getQuoteType(state, quoteId) ?? 'Indicative';
    const workspaceType = tab.workspace;

    switch (workspaceType) {
      case 'static':
        if (status === 'Tradeable') {
          dispatch(staticQuoteTradeCancelledThunk(tab.tabId));
        }
        dispatch(staticQuoteRequestedThunk(tab.tabId, quoteType, quoteId));
        break;
      case 'dynamic':
        if (status === 'Tradeable') {
          dispatch(dynamicQuoteTradeCancelledThunk(tab.tabId));
        }

        dispatch(dynamicQuoteRequestedThunk(tab.tabId, quoteType, quoteId));
        break;
    }
  };
}

export function dashboardLoadedThunk(
  quoteIds: string[],
  metadata: Collection<TileMetadata>,
  tiles: Collection<ReloadedTileData>,
): Thunk<void> {
  return (dispatch, _getState, { getNow }) => {
    const today = getDateOnlyFromDate(getNow());
    const obsoleteQuoteIds = findQuoteTooOld(tiles, metadata, today);

    dispatch(dashboardLoaded(quoteIds, metadata, tiles));
    if (obsoleteQuoteIds.length > 0) {
      dispatch(dashboardObsoleteQuotesDeleted(obsoleteQuoteIds));
      getFrontLogger().info(
        'Dashboard > deleting {count}/{total} quotes tiles which are too old.',
        obsoleteQuoteIds.length,
        quoteIds.length,
      );
    } else {
      getFrontLogger().info('Dashboard > all {total} quote tiles were kept.', quoteIds.length);
    }
  };
}
